/**
 * One-shot recovery for a user dropped while on /play (2026-10-15, QA 10.10
 * #13). Core socket.ts nulls the user on any handshake AUTH_FAILED, and the
 * handshake only sees the 15-minute access cookie — so a socket reconnect
 * after that cookie lapsed signed the player out with a valid 3-day refresh
 * cookie. One refresh attempt fixes that case.
 *
 * Rate-limited (2026-10-16): if the refresh succeeds but the socket keeps
 * failing (JWT secret mismatch, cookie not reaching a cross-origin WS), an
 * unguarded retry loops refresh → connect → fail forever. A second drop
 * within `windowMs` of the last attempt is treated as a real sign-out.
 */
export interface SessionRecoveryDeps {
  /** Tries the refresh cookie; resolves truthy when the session is back. */
  refresh: () => Promise<unknown>;
  onRecovered: () => void;
  onGiveUp: () => void;
  now?: () => number;
  windowMs?: number;
}

export const RECOVERY_WINDOW_MS = 30_000;

/** Returns the handler to call each time the user is found null on /play. */
export function createSessionRecovery(deps: SessionRecoveryDeps): () => void {
  const now = deps.now ?? Date.now;
  const windowMs = deps.windowMs ?? RECOVERY_WINDOW_MS;
  let inFlight = false;
  let lastAttemptAt = -Infinity;
  return () => {
    // The refresh's own failure path writes user:null again — ignore it.
    if (inFlight) return;
    if (now() - lastAttemptAt < windowMs) { deps.onGiveUp(); return; }
    inFlight = true;
    lastAttemptAt = now();
    void deps.refresh()
      .catch(() => null)
      .then((ok) => {
        inFlight = false;
        if (ok) deps.onRecovered(); else deps.onGiveUp();
      });
  };
}
